import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock, AlertTriangle } from "lucide-react";

export function PaymentSuccessPage() {
  const [status, setStatus] = useState("pending"); // pending | active | pending_long
  const [tries, setTries] = useState(0);
  const auth = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    let stop = false;
    // No 1º load, dispara um resync — pega o pagamento recém-feito no Asaas
    // sem precisar esperar o webhook. Depois faz polling do status local.
    const kickResync = async () => {
      try { await api.post("/payments/asaas/resync"); } catch { /* ignore */ }
    };
    const firePurchasePixel = async () => {
      try {
        const { data } = await api.get("/marketing/purchase-event");
        if (data?.has_event && data.event_id) {
          const { trackPurchase } = await import("@/lib/pixel");
          trackPurchase({ eventID: data.event_id, value: data.value, currency: data.currency });
        }
      } catch { /* opcional */ }
    };
    let attempts = 0;
    const poll = async () => {
      if (stop) return;
      attempts++;
      if (attempts > 1 && attempts % 2 === 0) {
        await kickResync();
      }
      try {
        const { data } = await api.get("/payments/asaas/status");
        if (data.plan === "premium" && (data.subscription_source === "asaas" || data.subscription_source === "payment")) {
          setStatus("active");
          await auth.refreshUsage?.();
          await firePurchasePixel();
          setTimeout(() => nav("/dashboard"), 2500);
          return;
        }
      } catch { /* ignore */ }
      if (!stop && attempts < 24) {
        setTries(t => t + 1);
        setTimeout(poll, 2500);
      } else if (!stop) {
        setStatus("pending_long");
      }
    };
    kickResync().finally(() => { if (!stop) poll(); });
    return () => { stop = true; };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6"
         style={{ background: "linear-gradient(135deg, #1F3A8A 0%, #8B5CF6 100%)" }}>
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
        <Logo />
        {status === "pending" && (<>
          <Loader2 className="animate-spin mx-auto mt-6 text-[#8B5CF6]" size={44}/>
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900" data-testid="payment-pending-title">Confirmando pagamento...</h1>
          <p className="text-slate-600 mt-2">Aguarde alguns segundos enquanto o Asaas nos confirma.</p>
        </>)}
        {status === "pending_long" && (<>
          <Clock className="mx-auto mt-6 text-amber-500" size={44}/>
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Pagamento em processamento</h1>
          <p className="text-slate-600 mt-2">
            O Asaas ainda não confirmou sua assinatura. Isso pode levar alguns minutos
            (especialmente para PIX). Você receberá acesso Premium automaticamente
            assim que a confirmação chegar.
          </p>
          <Link to="/dashboard"><Button className="mt-6 rounded-full bg-[#1F3A8A]" data-testid="back-dashboard">Ir para o painel</Button></Link>
        </>)}
        {status === "active" && (<>
          <CheckCircle2 className="mx-auto mt-6 text-emerald-500" size={48}/>
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900" data-testid="payment-success-title">Pagamento confirmado!</h1>
          <p className="text-slate-600 mt-2">Bem-vindo(a) ao AulaPronta Premium. Redirecionando...</p>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
        <XCircle className="mx-auto text-slate-400" size={48}/>
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900" data-testid="payment-cancel-title">Pagamento cancelado</h1>
        <p className="text-slate-600 mt-2">A contratação não foi concluída. Você pode tentar novamente a qualquer momento.</p>
        <Link to="/assinatura"><Button className="mt-6 rounded-full bg-[#1F3A8A]" data-testid="back-subscription">Voltar para a assinatura</Button></Link>
      </div>
    </div>
  );
}

export function PaymentExpiredPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
        <AlertTriangle className="mx-auto text-amber-500" size={48}/>
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900" data-testid="payment-expired-title">Checkout expirado</h1>
        <p className="text-slate-600 mt-2">O tempo para concluir o pagamento acabou. Nenhum valor foi cobrado — gere um novo Checkout quando quiser.</p>
        <Link to="/assinatura"><Button className="mt-6 rounded-full bg-[#1F3A8A]" data-testid="back-subscription-expired">Gerar novo Checkout</Button></Link>
      </div>
    </div>
  );
}
